import { AnnouncementBar } from '@theme/announcement-bar';

/** Small fudge so sub-pixel rounding doesn't flip mode unnecessarily. */
const FITS_BUFFER_PX = 4;

/**
 * Time after the last ResizeObserver callback before slide transitions
 * are re-enabled. Long enough to bridge the gap between successive RO
 * callbacks during a continuous user resize gesture, short enough that
 * the carousel's auto-rotation fade resumes promptly once resizing
 * stops.
 */
const INSTANT_CLEANUP_DELAY_MS = 120;

/**
 * Responsive variant of the announcement bar carousel.
 *
 * Uses a ResizeObserver-driven measurement pass (rather than a viewport
 * media query) to decide when all announcements fit side-by-side in their
 * container. When they do, autoplay is paused, the live region is silenced
 * and every slide is revealed at once. When they don't fit, the bar falls
 * back to the auto-rotating single-slide carousel.
 *
 * Measurement strategy: the host `aside` is briefly tagged with
 * `[data-measuring]`, which (via the section's stylesheet) flips the slides
 * container to `flex: row nowrap`. We then read the slides container's
 * `scrollWidth` (natural unwrapped width) and compare it to its
 * `clientWidth` (available width). The measurement DOM mutation is purely
 * synchronous, so no paint occurs between flipping and reading.
 *
 * @extends {AnnouncementBar}
 */
export class AnnouncementBarResponsive extends AnnouncementBar {
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {'inline' | 'slider' | null} */
  #mode = null;

  /** Cached `aria-live` value while the bar is in inline mode. */
  #ariaLiveSnapshot = null;

  /** @type {HTMLElement | null} */
  #aside = null;

  /** @type {HTMLElement | null} */
  #slidesEl = null;

  /**
   * Pending timer id for the deferred `data-instant` cleanup. We cancel
   * and re-schedule on every measurement so a continuous resize keeps
   * slide transitions suppressed for its entire duration; cleanup only
   * runs once the resize quiets down for {@link INSTANT_CLEANUP_DELAY_MS}.
   * @type {number | undefined}
   */
  #cleanupTimerId;

  connectedCallback() {
    super.connectedCallback();

    this.#aside = this.closest('aside');
    this.#slidesEl = this.querySelector('.announcement-bar__slides');

    if (!this.#aside || !this.#slidesEl) return;

    this.#resizeObserver = new ResizeObserver(() => this.#measureAndUpdate());
    this.#resizeObserver.observe(this.#aside);

    // Initial measurement (also fires via ResizeObserver but run sync to avoid
    // a one-frame flash of slider mode on desktop loads).
    this.#measureAndUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    if (this.#cleanupTimerId !== undefined) {
      clearTimeout(this.#cleanupTimerId);
      this.#cleanupTimerId = undefined;
    }
  }

  #measureAndUpdate() {
    if (!this.#aside || !this.#slidesEl) return;

    // Toggle the measurement layout (inline + nowrap) on, read both the
    // natural (unwrapped) width and the available width, then toggle off.
    // Browser doesn't paint between these synchronous DOM ops.
    this.#aside.setAttribute('data-measuring', '');
    const naturalWidth = this.#slidesEl.scrollWidth;
    const availableWidth = this.#slidesEl.clientWidth;
    this.#aside.removeAttribute('data-measuring');

    const fits = naturalWidth <= availableWidth + FITS_BUFFER_PX;
    const targetMode = fits ? 'inline' : 'slider';
    const previousMode = this.#mode;

    // Suppress slide opacity/visibility transitions for the duration of
    // the resize gesture. Without this, a slider-mode mid-fade caught by
    // the user's resize action shows two slides at partial opacity
    // stacked at the same grid cell. We re-arm the cleanup rAF on every
    // measurement, so the suppression only lifts once ≈2 frames pass
    // with no further ResizeObserver activity.
    if (previousMode !== null) {
      this.#aside.setAttribute('data-instant', '');
      this.#scheduleInstantCleanup();
    }

    // Update data-fits authoritatively so the pre-JS CSS heuristic stops
    // applying once we've measured.
    this.#aside.setAttribute('data-fits', String(fits));

    if (targetMode === previousMode) return;

    this.#mode = targetMode;

    if (targetMode === 'inline') {
      this.#enterInlineMode();
    } else {
      this.#enterSliderMode();
    }

    // Force a layout flush so the new state is committed in this same
    // task without animating, before the browser gets a chance to paint.
    void this.#slidesEl.offsetWidth;
  }

  /**
   * (Re)schedules the deferred removal of `data-instant`. Cancelling any
   * previously-pending cleanup ensures the suppression remains in place
   * across rapid back-to-back ResizeObserver callbacks. We use a debounce
   * timer rather than rAF chains because nested rAFs can fire in the gap
   * between two RO callbacks and prematurely lift the suppression.
   */
  #scheduleInstantCleanup() {
    if (this.#cleanupTimerId !== undefined) {
      clearTimeout(this.#cleanupTimerId);
    }
    this.#cleanupTimerId = window.setTimeout(() => {
      this.#cleanupTimerId = undefined;
      this.#aside?.removeAttribute('data-instant');
    }, INSTANT_CLEANUP_DELAY_MS);
  }

  #enterInlineMode() {
    // Use `pause()` (not just `suspend()`) so the inherited mouseleave
    // handler can't kick autoplay back on while we're inline.
    this.pause();

    if (this.hasAttribute('aria-live')) {
      this.#ariaLiveSnapshot = this.getAttribute('aria-live');
      this.removeAttribute('aria-live');
    }

    this.refs.slides?.forEach((slide) => {
      slide.setAttribute('aria-hidden', 'false');
    });
  }

  #enterSliderMode() {
    if (this.#ariaLiveSnapshot !== null) {
      this.setAttribute('aria-live', this.#ariaLiveSnapshot);
      this.#ariaLiveSnapshot = null;
    }

    // Re-apply aria-hidden state to slides for the current index.
    this.current = this.current;

    this.paused = false;
    this.play();
  }
}

if (!customElements.get('announcement-bar-responsive-component')) {
  customElements.define('announcement-bar-responsive-component', AnnouncementBarResponsive);
}
